import graphql__35db05c058257dbe30df from "./__generated__/ProjectList_query.graphql.ts";import * as React from "react"
import {useEffect, useTransition} from "react"
import {graphql} from "react-relay";
import {usePaginationFragment} from "react-relay/hooks";
import LoadMoreButton from "./presentational/LoadMoreButton";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData, {TextAlignment} from "./presentational/table/TableData";
import {resolvePath, useNavigate} from "react-router";
import ActionButton from "./presentational/button/ActionButton";
import {faFileMagnifyingGlass} from "@fortawesome/pro-regular-svg-icons";
import {hideModal, showModal} from "../store/actions";
import {connect, ConnectedProps} from "react-redux";
import Dinero from "dinero.js";
import StatusBadge from "./presentational/StatusBadge";
import {useIntl} from "react-intl";

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type Props = {
    queryRef: any // FIXME: Any type we can define here? It's actually: Readonly<{ ' $data'?: unknown; ' $fragmentRefs': unknown; }> | null
    searchTerm?: string
    onLoadingChange?: (isLoading: boolean) => void
}

type ProjectListProps = ConnectedProps<typeof connector> & Props;

const ProjectList: React.FC<ProjectListProps> = (props) => {
    const intl = useIntl();
    const [isPending, startTransition] = useTransition();
    const {data, hasNext, loadNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql__35db05c058257dbe30df,
        props.queryRef
    );
    const navigate = useNavigate();

    useEffect(() => {
        // Use startTransition to keep the old data visible while loading new data
        // This prevents the Suspense fallback from showing
        startTransition(() => {
            refetch({searchTerm: props.searchTerm}, {fetchPolicy: 'store-and-network'});
        });
    }, [props.searchTerm, refetch])

    useEffect(() => {
        // Notify parent component about loading state
        props.onLoadingChange?.(isPending);
    }, [isPending, props]);

    if (!data.projects || data.projects.totalCount === 0) {
        return (<></>);
    }

    return (
        <div className="flex-1 min-h-0 relative overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.projects'})}>
            <Table>
                <thead>
                    <tr>
                        <TableHeader>{intl.formatMessage({id: 'list.project.header.state'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.project.header.projectNumber'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.project.header.project'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.project.header.customer'})}</TableHeader>
                        <TableHeader className="text-right">{intl.formatMessage({id: 'list.project.header.rate'})}</TableHeader>
                        <TableHeader/>
                    </tr>
                </thead>
                <tbody>
                    {data.projects.edges.map(
                        (edge, index) => {
                            if (!edge.node) { // why can this happen?
                                return null;
                            }
                            const project = edge.node;
                            const {customerAccount} = project;
                            const {customerName} = customerAccount;
                            const projectRate = Dinero({amount: project.hourlyRate}).toFormat() + ' / h'
                            const projectTotal = Dinero({amount: (project.fixedFee ? project.fixedFee : 0)}).toFormat()

                            return (
                                <tr key={index} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                    <TableData>
                                        {project.state === 'ARCHIVED' && (<StatusBadge value={intl.formatMessage({id: 'list.project.state.archived'})} states={{}}/>)}
                                        {project.state === 'ACTIVE' && (<StatusBadge value={intl.formatMessage({id: 'list.project.state.active'})} states={{green: [intl.formatMessage({id: 'list.project.state.active'})]}}/>)}
                                    </TableData>
                                    <TableData>{project.projectNumber}</TableData>
                                    <TableData>{project.projectName}</TableData>
                                    <TableData>{customerName}</TableData>
                                    <TableData textAlignment={TextAlignment.Right}>
                                        {project.billingMode === 'TIME_AND_MATERIALS' && projectRate}
                                        {project.billingMode === 'NON_BILLABLE' && (<StatusBadge value={intl.formatMessage({id: 'list.project.billingMode.nonBillable'})} states={{}}/>)}
                                        {project.billingMode === 'FIXED_FEE' && (
                                            <>
                                                <StatusBadge value={intl.formatMessage({id: 'list.project.billingMode.fixedFee'})} states={{green: [intl.formatMessage({id: 'list.project.billingMode.fixedFee'})]}}/>
                                                {" "}
                                                {projectTotal}
                                            </>
                                        )}
                                    </TableData>
                                    <TableData className="hidden sm:table-cell" textAlignment={TextAlignment.Right}>
                                        <ActionButton additionalClassname="invisible group-hover:visible" icon={faFileMagnifyingGlass} title={intl.formatMessage({id: 'list.project.action.show'})} onClick={(event) => {
                                            let to = "/projects/" + project.id + "/details";
                                            if (event.shiftKey || event.metaKey) {
                                                event.preventDefault()
                                                window.open(resolvePath(to).pathname)
                                            } else {
                                                navigate(to)
                                            }
                                        }}/>
                                    </TableData>
                                </tr>
                            )
                        }
                    )}
                </tbody>
            </Table>
            {hasNext && <LoadMoreButton onClick={() => loadNext(100)} isLoadingNext={isLoadingNext}/>}
        </div>
    )
}

export default connector(ProjectList);
